import React, { forwardRef } from 'react';
import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import type { ContentProps, TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import type { CustomTriggerComponentType } from '../types.tsx';
import { EntryPointContent } from './entrypoint-content/index.tsx';
import messages from './messages.tsx';

const CustomTrigger: CustomTriggerComponentType = forwardRef<HTMLButtonElement, TriggerProps>(
	({ ...props }, ref) => {
		const { formatMessage } = useIntl();

		const triggerButton = (
			<ThemedButton
				{...props}
				ref={ref}
				appearance="subtle"
				iconBefore={
					<ShowMoreIcon
						label={formatMessage(messages.moreActions)}
						color="currentColor"
						spacing="spacious"
					/>
				}
				testId="navigation-project-action-menu.ui.themed-button"
			/>
		);

		return isVisualRefreshEnabled() ? (
			<Tooltip content={formatMessage(messages.moreActions)} hideTooltipOnClick>
				{triggerButton}
			</Tooltip>
		) : (
			triggerButton
		);
	},
);

type Props = {
	projectKey: string;
	isCorePremiumUserSeat?: boolean;
	isProjectMenuReorderingEnabled?: boolean;
	renderAdditionalMenuItems?: (params?: ContentProps) => React.ReactNode;
	withThemedTrigger?: boolean;
	onClick?: () => void;
	starred?: boolean;
	recent?: boolean;
	shouldRenderToParent?: boolean;
	themePicker?: React.ReactNode;
	projectName?: string;
};

export const ProjectActionMenu = ({
	renderAdditionalMenuItems,
	isProjectMenuReorderingEnabled = false,
	isCorePremiumUserSeat = false,
	withThemedTrigger = false,
	projectKey,
	onClick,
	shouldRenderToParent,
	themePicker,
	projectName,
}: Props) => {
	return (
		<EntryPointContent
			{...(fg('jsm_aiiy_for_nav_item_sidebar') ? { projectName } : {})}
			isProjectMenuReorderingEnabled={isProjectMenuReorderingEnabled}
			renderAdditionalMenuItems={renderAdditionalMenuItems}
			onClick={onClick}
			isCorePremiumUserSeat={isCorePremiumUserSeat}
			CustomTrigger={withThemedTrigger && getWillShowNav4() ? CustomTrigger : undefined}
			projectKey={projectKey}
			shouldRenderToParent={shouldRenderToParent}
			{...(getIsSoftwareThemingM2Enabled() && { themePicker })}
		/>
	);
};
